import { useCallback, useState, useEffect } from "react";
import { Box, TextField, MenuItem, InputAdornment, Tooltip } from "@mui/material";
import { columnPresets, getPresetColumns } from "../../hooks/useTableColumnsConfig";
import { useMainContext } from "../../../../context/useMainContext";
import saveColumnPreferences from "../../../../utils/saveColumnPreferences";
import { updateUrlParams } from "../../../../utils/urlParams";
import SearchIcon from "@mui/icons-material/Search";
import FilterSection from "./FilterSection";
import DotsMenu from "./DotsMenu";
import ChecksMenu from "./ChecksMenu";
import { useDebouncedCallback } from "use-debounce";
import AddPartner from "./AddPartner";
import UndoButton from "./UndoButton";

export default function Actions() {
  const {
    columns,
    setVisibleColumns,
    setCurrentColumnPreset,
    isInitialLoad,
    isGuest,
    searchQuery,
    setSearchQuery,
    currentColumnPreset,
    setPage,
  } = useMainContext();
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [isDotsMenuOpen, setIsDotsMenuOpen] = useState(null);
  const [anchorExportEl, setAnchorExportEl] = useState(null);

  const handleSearchQueryDebounced = useDebouncedCallback((e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchQuery(searchValue);
    setPage(0);
    if (!isInitialLoad) {
      updateUrlParams({ searchQuery: searchValue, page: 0 });
    }
  }, 500);

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  const handlePresetChange = useCallback(
    (presetName) => {
      setCurrentColumnPreset(presetName);
      const presetColumns = getPresetColumns({ presetKey: presetName });
      setVisibleColumns(presetColumns);
      saveColumnPreferences({ presetKey: presetName, columns: presetColumns });
    },
    [
      setCurrentColumnPreset,
      columnPresets,
      columns,
      setVisibleColumns,
      saveColumnPreferences,
    ]
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flex: 1,
        }}
      >
        <TextField
          placeholder="Search any column"
          value={localSearchQuery}
          onChange={(e) => {
            setLocalSearchQuery(e.target.value);
            handleSearchQueryDebounced(e);
          }}
          variant="outlined"
          size="small"
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            maxWidth: "400px",
          }}
        />
        <FilterSection />
        <UndoButton />
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Tooltip title="Columns View" arrow placement="top">
          <TextField
            select
            value={currentColumnPreset || "default"}
            onChange={(e) => !isGuest && handlePresetChange(e.target.value)}
            disabled={isGuest}
            size="small"
          >
            {Object.keys(columnPresets).map((preset) => (
              <MenuItem key={preset} value={preset}>
                {columnPresets[preset].label}
              </MenuItem>
            ))}
          </TextField>
        </Tooltip>

        <ChecksMenu />

        <AddPartner />

        {!isGuest && (
          <DotsMenu
            {...{
              setAnchorExportEl,
              isDotsMenuOpen,
              setIsDotsMenuOpen,
              anchorExportEl
            }}
          />
        )}
      </Box>
    </Box>
  );
}
